import { ClaimItemDTO } from '@reposit/api-client';
import { startCase, toLower } from 'lodash';
import numeral from 'numeral';
import React from 'react';
import styled from 'styled-components';
import { NumberFormat } from '../../constants/number-formats';
import { FLASH_MESSAGE_TIMEOUT, useFlashMessage } from '../../containers/FlashMessage';
import { DELETE_CLAIM_STORE_KEY, PREVIEW_DELETE_CLAIM_STORE_KEY } from '../../redux/claim/claim.actions';
import { ClaimDeletionPreview } from '../../redux/claim/claim.types';
import Button from '../Button';
import FlashMessage from '../FlashMessage';
import { getChargeItemTitle } from '../../utils/charge-item-title';
import IdentifierValue from '../IdentifierValue';
import Loading from '../Loading';
import Modal from '../Modal';
import { Header3, P2 } from '../Typography';

interface DeleteClaimModalProps {
  onDismiss: () => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  isPreviewLoading: boolean;
  preview?: ClaimDeletionPreview;
  claimId: string;
  claimAddress?: string;
  claimItems?: ClaimItemDTO[];
}

const ItemSummary = styled.div`
  background: #fff;
  border: 1px solid ${(props) => props.theme.colors.dividerLight};
  border-radius: 8px;
  margin-bottom: 8px;
  overflow: hidden;
  padding: 22px 14px 14px;
  position: relative;
`;

const CardTypePill = styled.span`
  background: ${(props) => props.theme.colors.bgHoverAccent};
  border-bottom-right-radius: 4px;
  color: ${(props) => props.theme.colors.tertiaryBrand};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  left: 0;
  letter-spacing: 0.07em;
  padding: 3px 8px;
  position: absolute;
  text-transform: uppercase;
  top: 0;
`;

const SectionHeader = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
  margin-bottom: 12px;
  margin-top: 18px;

  &::before,
  &::after {
    background: ${(props) => props.theme.colors.negative};
    content: '';
    flex: 1;
    height: 1px;
    opacity: 0.4;
  }
`;

const SectionHeaderText = styled.span`
  color: ${(props) => props.theme.colors.negative};
  flex-shrink: 0;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

const ItemSummaryTopRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
`;

const ItemSummaryLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;

const ItemSummaryName = styled.span`
  color: ${(props) => props.theme.colors.body};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
`;

const ItemSummarySubRow = styled.div`
  color: ${(props) => props.theme.colors.placeholder};
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 12px;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ItemSummaryAmount = styled.span`
  align-items: center;
  align-self: center;
  color: ${(props) => props.theme.colors.body};
  display: flex;
  font-family: ${(props) => props.theme.typography.face.secondary};
  font-size: 15px;
  font-weight: 700;
  gap: 6px;
  flex-shrink: 0;
  margin-left: 12px;
`;

const ActionBar = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  padding-top: 16px;

  button {
    margin: 0;
  }
`;

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 24px 0;
`;

const formatAmount = (pence: number) => `£${numeral(pence / 100).format(NumberFormat.THOUSANDS_PENCE)}`;
const formatType = (type: string) => startCase(toLower(type.replace(/_/g, ' ')));

interface SummaryCardProps {
  pill: string;
  name: React.ReactNode;
  subRow?: React.ReactNode;
  amount: number;
}

const SummaryCard: React.FC<SummaryCardProps> = ({ pill, name, subRow, amount }) => (
  <ItemSummary>
    <CardTypePill>{pill}</CardTypePill>
    <ItemSummaryTopRow>
      <ItemSummaryLeft>
        <ItemSummaryName>{name}</ItemSummaryName>
        {subRow ? <ItemSummarySubRow>{subRow}</ItemSummarySubRow> : null}
      </ItemSummaryLeft>
      <ItemSummaryAmount>{formatAmount(amount)}</ItemSummaryAmount>
    </ItemSummaryTopRow>
  </ItemSummary>
);

const DeleteClaimModal: React.FC<DeleteClaimModalProps> = ({
  onDismiss,
  onSubmit,
  isSubmitting,
  isPreviewLoading,
  preview,
  claimId,
  claimAddress,
  claimItems = [],
}) => {
  const [flashMessagePayload, dismissFlashMessage] = useFlashMessage([DELETE_CLAIM_STORE_KEY, PREVIEW_DELETE_CLAIM_STORE_KEY]);

  const activeClaimItems = claimItems.filter((item) => !item.deletedAt);
  const claimTotal = activeClaimItems.reduce((sum, item) => sum + item.originalAmount, 0);
  const blockingReasons = preview ? preview.blockingReasons : [];
  const isDeletionBlocked = blockingReasons.length > 0;

  return (
    <Modal onDismiss={onDismiss}>
      {flashMessagePayload ? (
        <FlashMessage payload={flashMessagePayload} onDismiss={dismissFlashMessage} timeRemaining={FLASH_MESSAGE_TIMEOUT} />
      ) : null}

      <Header3 style={{ marginBottom: 4 }}>Delete Claim</Header3>
      <P2 style={{ marginBottom: 20 }}>Review the records below before proceeding. This action cannot be undone.</P2>

      {isPreviewLoading || !preview ? (
        <LoadingWrapper>
          <Loading />
        </LoadingWrapper>
      ) : (
        <>
          <SectionHeader>
            <SectionHeaderText>Will be deleted</SectionHeaderText>
          </SectionHeader>

          <SummaryCard
            pill="Claim"
            name={claimAddress || 'Claim'}
            subRow={<IdentifierValue>{claimId}</IdentifierValue>}
            amount={claimTotal}
          />

          {activeClaimItems.map((item) => (
            <SummaryCard
              key={item.id}
              pill="Claim item"
              name={getChargeItemTitle(item)}
              subRow={
                <>
                  <IdentifierValue>{item.id}</IdentifierValue>
                  {item.type ? ` · ${formatType(item.type)}` : null}
                </>
              }
              amount={item.originalAmount}
            />
          ))}
        </>
      )}

      <ActionBar>
        <Button buttonType="secondary" onClick={onDismiss} disabled={isSubmitting} noArrow>
          Go back
        </Button>
        <Button
          buttonType="warning"
          onClick={onSubmit}
          disabled={isSubmitting || isPreviewLoading || !preview || isDeletionBlocked}
        >
          Delete
        </Button>
      </ActionBar>
    </Modal>
  );
};

export default DeleteClaimModal;
